import { RequestDecisionDialog } from "./presentation/requests/detail/RequestDecisionDialog";
import { RequestContextPanel } from "./presentation/requests/detail/RequestContextPanel";
import { RequestEditForm } from "./presentation/requests/detail/RequestEditForm";
import { RequestOverview } from "./presentation/requests/detail/RequestOverview";
import { RequestOffersPanel } from "./presentation/requests/detail/RequestOffersPanel";

import { requestDetailViewModel } from "./presentation/requests/detail/request-detail-view-model";
import { useRequestDetailWorkspace } from "./presentation/requests/detail/useRequestDetailWorkspace";

import { RequestAttachmentPreview } from "./presentation/requests/detail/RequestAttachmentPreview";

import { RevisionComparison } from "./presentation/shared/RevisionComparison";
export { RevisionComparison } from "./presentation/shared/RevisionComparison";
export type { ProcurementRevision, RequestDetailData } from "./domain/request-detail-types";
import { Clock3, FileText } from "lucide-react";
import { useMemo, useState } from "react";

import { can, type SessionUser } from "../../shared/auth/access";

export function RequestDetail({
  requestNo,
  onBack,
  notify,
  user,
  presentation = "page",
  onRequestRoutedForApproval,
}: {
  requestNo: string;
  onBack: () => void;
  notify: (message: string) => void;
  user: SessionUser;
  presentation?: "page" | "workspace";
  onRequestRoutedForApproval?: () => void;
}) {

  const {
    detail,
    loading,
    evaluation,
    decisionOpen,
    decisionStep,
    decisionOfferId,
    decisionRationale,
    decisionError,
    setDecisionOpen,
    setDecisionStep,
    setDecisionOfferId,
    setDecisionRationale,
    openDecision,
    submitDecision,
    routeForApproval,
    refreshEvaluation,
    evaluationError,
    refreshingEvaluation,
    submittingDecision,
    requestDecisionBusy,
    selectedDecisionOffer,
    systemDecisionOffer,
    systemDecisionRunnerUp,
    systemDecisionScoreGap,
    systemRecommendationSummary,
    systemRecommendationMetrics,
    rationalePrompts,
    previewAttachment,
    setPreviewAttachment,
    openOfferDocumentsId,
    setOpenOfferDocumentsId,
    previewObjectUrl,
    previewError,
    offerComposerOpen,
    suppliersLoading,
    savingOffer,
    editingOfferId,
    pendingOfferRemovalId,
    setPendingOfferRemovalId,
    removingOfferId,
    offerFormError,
    offerDocument,
    setOfferDocument,
    extraCostsOpen,
    setExtraCostsOpen,
    offerForm,
    setOfferForm,
    supplierAutocompleteOptions,
    directOfferTotal,
    deliveryWarning,
    duplicateSupplierOffer,
    openOfferComposer,
    openOfferEditor,
    closeOfferComposer,
    saveDirectOffer,
    removeOffer,
    editing,
    saving,
    editForm,
    setEditing,
    setEditForm,
    beginEditing,
    saveRequest,
  } = useRequestDetailWorkspace({requestNo, user, notify, onRequestRoutedForApproval});
  const [contextExpanded, setContextExpanded] = useState(false);
  const viewModel = useMemo(() => detail ? requestDetailViewModel(detail, evaluation, user) : null, [detail, evaluation, user]);

  if (loading) {
    return (
      <section className="request-detail-state">
        <Clock3 size={24} />
        <strong>Talep detayı yükleniyor</strong>
      </section>
    );
  }

  if (!detail || !viewModel) {
    return (
      <section className="request-detail-state">
        <FileText size={24} />
        <strong>Talep detayı görüntülenemedi</strong>
        <button className="secondary-button" onClick={onBack}>
          Listeye dön
        </button>
      </section>
    );
  }

  const {
    comparison,
    getAttachmentOffer,
    getOfferAttachments,
    activeCorrection,
    isCompleted,
    managerDecision,
    generalManagerDecision,
    finalSelectionChanged,
    completedDecisionDataMissing,
    detailFlowSteps,
    specialistFlowPresentation,
    canRouteRequest,
    canEditRequest,
    eligibleDecisionOffers,
    canManageSpecialistDecision,
    canManageOffers,
    canMakeSpecialistDecision,
    decisionPanelContent,
  } = viewModel;
  return (
    <div
      className={`request-detail${presentation === "workspace" ? " is-workspace" : ""}`}
    >
      <RequestOverview
        notify={notify}
        user={user}
        presentation={presentation}
        detail={detail}
        routeForApproval={routeForApproval}
        requestDecisionBusy={requestDecisionBusy}
        beginEditing={beginEditing}
        activeCorrection={activeCorrection}
        detailFlowSteps={detailFlowSteps}
        specialistFlowPresentation={specialistFlowPresentation}
        canRouteRequest={canRouteRequest}
        canEditRequest={canEditRequest}
      />

      <RevisionComparison revisions={detail.revisions ?? []} />

      <RequestEditForm
        editing={editing}
        saving={saving}
        editForm={editForm}
        setEditing={setEditing}
        setEditForm={setEditForm}
        saveRequest={saveRequest}
        isCompleted={isCompleted}
      />

      <div className="detail-grid">
        <div className="detail-main">
          {can(user, "offer.read") ? (
            <RequestOffersPanel
              detail={detail}
              setPreviewAttachment={setPreviewAttachment}
              openOfferDocumentsId={openOfferDocumentsId}
              setOpenOfferDocumentsId={setOpenOfferDocumentsId}
              offerComposerOpen={offerComposerOpen}
              suppliersLoading={suppliersLoading}
              savingOffer={savingOffer}
              editingOfferId={editingOfferId}
              pendingOfferRemovalId={pendingOfferRemovalId}
              setPendingOfferRemovalId={setPendingOfferRemovalId}
              removingOfferId={removingOfferId}
              offerFormError={offerFormError}
              offerDocument={offerDocument}
              setOfferDocument={setOfferDocument}
              extraCostsOpen={extraCostsOpen}
              setExtraCostsOpen={setExtraCostsOpen}
              offerForm={offerForm}
              setOfferForm={setOfferForm}
              supplierAutocompleteOptions={supplierAutocompleteOptions}
              directOfferTotal={directOfferTotal}
              deliveryWarning={deliveryWarning}
              duplicateSupplierOffer={duplicateSupplierOffer}
              openOfferComposer={openOfferComposer}
              openOfferEditor={openOfferEditor}
              closeOfferComposer={closeOfferComposer}
              saveDirectOffer={saveDirectOffer}
              removeOffer={removeOffer}
              comparison={comparison}
              getOfferAttachments={getOfferAttachments}
              canManageOffers={canManageOffers}
            />
          ) : null}
        </div>

        <RequestContextPanel
          user={user}
          detail={detail}
          evaluation={evaluation}
          evaluationError={evaluationError}
          openDecision={openDecision}
          refreshEvaluation={refreshEvaluation}
          refreshingEvaluation={refreshingEvaluation}
          requestDecisionBusy={requestDecisionBusy}
          contextExpanded={contextExpanded}
          setContextExpanded={setContextExpanded}
          activeCorrection={activeCorrection}
          isCompleted={isCompleted}
          managerDecision={managerDecision}
          generalManagerDecision={generalManagerDecision}
          finalSelectionChanged={finalSelectionChanged}
          completedDecisionDataMissing={completedDecisionDataMissing}
          canManageSpecialistDecision={canManageSpecialistDecision}
          canMakeSpecialistDecision={canMakeSpecialistDecision}
          decisionPanelContent={decisionPanelContent}
        />
      </div>
      <RequestDecisionDialog
        evaluation={evaluation}
        decisionOpen={decisionOpen}
        decisionStep={decisionStep}
        decisionOfferId={decisionOfferId}
        decisionRationale={decisionRationale}
        decisionError={decisionError}
        setDecisionOpen={setDecisionOpen}
        setDecisionStep={setDecisionStep}
        setDecisionOfferId={setDecisionOfferId}
        setDecisionRationale={setDecisionRationale}
        submitDecision={submitDecision}
        submittingDecision={submittingDecision}
        requestDecisionBusy={requestDecisionBusy}
        selectedDecisionOffer={selectedDecisionOffer}
        systemDecisionOffer={systemDecisionOffer}
        systemDecisionRunnerUp={systemDecisionRunnerUp}
        systemDecisionScoreGap={systemDecisionScoreGap}
        systemRecommendationSummary={systemRecommendationSummary}
        systemRecommendationMetrics={systemRecommendationMetrics}
        rationalePrompts={rationalePrompts}
        eligibleDecisionOffers={eligibleDecisionOffers}
      />
      <RequestAttachmentPreview
        previewAttachment={previewAttachment}
        previewObjectUrl={previewObjectUrl}
        previewError={previewError}
        setPreviewAttachment={setPreviewAttachment}
        getAttachmentOffer={getAttachmentOffer}
      />
    </div>
  );
}
